// =====================================================================
// paginas/inicio.js — Dashboard diário.
//
// Critério para um número entrar aqui: ele muda o que alguém FAZ hoje.
// Métrica que não muda comportamento é enfeite, e enfeite treina a pessoa
// a ignorar a tela inteira.
//
// Linha 1 — o dinheiro do mês (previsto, recebido, atraso, conferência)
// Linha 2 — o patrimônio (unidades, ocupação, vagas, receita potencial)
// Linha 3 — as filas de trabalho: quem está devendo, o que conferir,
//           chamado aberto, contrato a renovar
// Linha 4 — os últimos 6 meses, pra ver tendência sem abrir relatório
// =====================================================================

import { sb } from '../../../../packages/ui/cliente.js';
import { icon, escapeHtml, tabela, badge } from '../ui.js';
import { moeda, data, competencia, diasAtraso } from '../../../../packages/ui/format.js';
import { abrir } from '../main.js';

export async function pagInicio(el, estado) {
  const hoje = new Date();
  const iso  = (d) => d.toISOString().slice(0, 10);
  const hj   = iso(hoje);
  const mes1 = hj.slice(0, 7) + '-01';
  const em90 = iso(new Date(Date.now() + 90 * 864e5));
  const seis = iso(new Date(hoje.getFullYear(), hoje.getMonth() - 5, 1));

  const [mes, vencidas, conferencia, chamados, renovar, imoveis, historico, contratos] =
    await Promise.all([
      sb.from('cobrancas').select('valor_total,valor_pago,status').gte('competencia', mes1),

      sb.from('cobrancas')
        .select('id,competencia,vencimento,valor_total,valor_pago,multa,juros,'
              + 'contratos(imoveis(codigo),inquilinos(nome))')
        .in('status', ['aberta', 'vencida', 'paga_parcial'])
        .lt('vencimento', hj)
        .order('vencimento'),

      sb.from('cobrancas')
        .select('id,competencia,valor_total,comprovante_valor_ocr,contratos(inquilinos(nome))')
        .eq('status', 'em_conferencia'),

      sb.from('chamados')
        .select('id,numero,titulo,prioridade,criado_em,imoveis(codigo)')
        .in('status', ['aberto', 'em_analise'])
        .order('criado_em'),

      sb.from('contratos')
        .select('id,numero,data_fim,valor_aluguel,imoveis(codigo),inquilinos(nome)')
        .eq('status', 'ativo').lte('data_fim', em90).order('data_fim'),

      sb.from('imoveis').select('id,status,valor_aluguel_ref').neq('status', 'inativo'),

      sb.from('cobrancas').select('competencia,valor_total,valor_pago').gte('competencia', seis),

      sb.from('contratos').select('id,valor_aluguel').eq('status', 'ativo')
    ]);

  // ── Se o banco recusou, a tela NÃO inventa zero ────────────────────
  //
  // Isto aqui já custou caro: uma recursão na RLS fez o Postgres recusar as
  // consultas, e o dashboard exibiu "R$ 0,00 em atraso" e "0 unidades" com
  // a maior tranquilidade. Zero e "não consegui ler" são coisas diferentes,
  // e confundir as duas num painel financeiro é pior que a tela cair: uma
  // tela caída todo mundo percebe; um zero errado alguém acredita.
  const respostas = { mes, vencidas, conferencia, chamados, renovar, imoveis, historico, contratos };
  const falhas = Object.entries(respostas)
    .filter(([, r]) => r && r.error)
    .map(([nome, r]) => `${nome}: ${r.error.message}`);

  if (falhas.length) {
    el.innerHTML = `
      <div class="cabecalho"><h2>Início</h2></div>
      <div class="aviso erro">
        ${icon('erro')}
        <div>
          <strong>Não consegui ler os dados. Os números abaixo seriam mentira, então não vou mostrá-los.</strong>
          <ul style="margin:8px 0 0;padding-left:18px">
            ${falhas.map(f => `<li>${escapeHtml(f)}</li>`).join('')}
          </ul>
        </div>
      </div>`;
    return;
  }

  const m   = mes.data || [];
  const v   = vencidas.data || [];
  const c   = conferencia.data || [];
  const ch  = chamados.data || [];
  const r   = renovar.data || [];
  const im  = imoveis.data || [];
  const h   = historico.data || [];
  const ct  = contratos.data || [];

  // ── Dinheiro do mês ────────────────────────────────────────────────
  const previsto = m.reduce((s, x) => s + Number(x.valor_total || 0), 0);
  const recebido = m.reduce((s, x) => s + Number(x.valor_pago || 0), 0);
  const pct      = previsto > 0 ? Math.round(recebido / previsto * 100) : 0;

  const emAtraso = v.reduce((s, x) =>
    s + Number(x.valor_total || 0) + Number(x.multa || 0) + Number(x.juros || 0)
      - Number(x.valor_pago || 0), 0);

  // Inadimplência sobre o previsto — o número que o dono pergunta.
  const inadPct = previsto > 0 ? Math.round(emAtraso / previsto * 100) : 0;

  // ── Patrimônio ─────────────────────────────────────────────────────
  const total    = im.length;
  const alugados = im.filter(x => x.status === 'alugado').length;
  const vagos    = im.filter(x => x.status === 'disponivel').length;
  const manut    = im.filter(x => x.status === 'manutencao').length;
  const ocupacao = total > 0 ? Math.round(alugados / total * 100) : 0;

  // Quanto está parado na mesa: o aluguel de referência do que está vago.
  const perdido = im.filter(x => x.status === 'disponivel')
                    .reduce((s, x) => s + Number(x.valor_aluguel_ref || 0), 0);
  const contratado = ct.reduce((s, x) => s + Number(x.valor_aluguel || 0), 0);

  // ── Últimos 6 meses ────────────────────────────────────────────────
  const porMes = {};
  for (const x of h) {
    const k = String(x.competencia).slice(0, 7);
    porMes[k] = porMes[k] || { prev: 0, rec: 0 };
    porMes[k].prev += Number(x.valor_total || 0);
    porMes[k].rec  += Number(x.valor_pago || 0);
  }
  const meses = Object.keys(porMes).sort().slice(-6);
  const topo  = Math.max(1, ...meses.map(k => porMes[k].prev));

  const cor = (p) => (p >= 90 ? 'bom' : p >= 70 ? 'atencao' : 'alerta');

  // A data de hoje subiu para a faixa do topo (main.js): ela vale para o app
  // inteiro, não só para esta tela. Repeti-la aqui seria dizer duas vezes a
  // mesma coisa — e era o que espremia o cabeçalho.
  el.innerHTML = `
    <div class="cabecalho">
      <h2>Início</h2>
    </div>

    <!-- Linha 1: o dinheiro do mês -->
    <div class="grade k4" style="margin-bottom:16px">
      <div class="cartao kpi">
        <span class="rotulo">${icon('cobranca', 'ic-sm')} Previsto no mês</span>
        <span class="valor">${moeda(previsto)}</span>
        <span class="nota">${m.length} cobrança(s) emitida(s)</span>
      </div>
      <div class="cartao kpi ${cor(pct)}">
        <span class="rotulo">${icon('ok', 'ic-sm')} Recebido</span>
        <span class="valor">${moeda(recebido)}</span>
        <span class="nota">${pct}% do previsto</span>
        <div class="progresso"><i class="${pct >= 90 ? '' : pct >= 70 ? 'ambar' : 'vermelho'}"
             style="width:${Math.min(pct, 100)}%"></i></div>
      </div>
      <div class="cartao kpi ${emAtraso > 0 ? 'alerta' : 'bom'}">
        <span class="rotulo">${icon('atraso', 'ic-sm')} Em atraso</span>
        <span class="valor">${moeda(emAtraso)}</span>
        <span class="nota">${v.length} cobrança(s) · ${inadPct}% de inadimplência</span>
      </div>
      <div class="cartao kpi ${c.length ? 'atencao' : ''}">
        <span class="rotulo">${icon('comprovante', 'ic-sm')} Comprovantes na fila</span>
        <span class="valor">${c.length}</span>
        <span class="nota">${c.length ? 'o valor lido não bateu — conferir' : 'nada pendente'}</span>
      </div>
    </div>

    <!-- Linha 2: o patrimônio -->
    <div class="grade k4" style="margin-bottom:24px">
      <div class="cartao kpi">
        <span class="rotulo">${icon('imovel', 'ic-sm')} Unidades</span>
        <span class="valor">${total}</span>
        <span class="nota">${alugados} alugada(s) · ${manut} em manutenção</span>
      </div>
      <div class="cartao kpi ${cor(ocupacao)}">
        <span class="rotulo">${icon('ocupacao', 'ic-sm')} Taxa de ocupação</span>
        <span class="valor">${ocupacao}%</span>
        <div class="progresso"><i class="${ocupacao >= 90 ? '' : ocupacao >= 70 ? 'ambar' : 'vermelho'}"
             style="width:${ocupacao}%"></i></div>
      </div>
      <div class="cartao kpi ${vagos ? 'atencao' : 'bom'}">
        <span class="rotulo">${icon('vago', 'ic-sm')} Parado na mesa</span>
        <span class="valor">${moeda(perdido)}</span>
        <span class="nota">${vagos} unidade(s) vaga(s) — receita que não entra</span>
      </div>
      <div class="cartao kpi">
        <span class="rotulo">${icon('contrato', 'ic-sm')} Contratado por mês</span>
        <span class="valor">${moeda(contratado)}</span>
        <span class="nota">${ct.length} contrato(s) ativo(s)</span>
      </div>
    </div>

    <!-- Linha 3: filas de trabalho -->
    <div class="grade k2" style="margin-bottom:24px">
      <div class="cartao" data-ir="financeiro">
        <h3>${icon('atraso')} Aluguéis em atraso</h3>
        ${tabela([
          { th: 'Inquilino', td: x => escapeHtml(x.contratos?.inquilinos?.nome || '—') },
          { th: 'Imóvel',    td: x => escapeHtml(x.contratos?.imoveis?.codigo || '—') },
          { th: 'Venceu',    td: x => {
              const d = diasAtraso(x.vencimento);
              return `${data(x.vencimento)} ${badge(`${d}d`, d > 15 ? 'vermelho' : 'ambar')}`;
            } },
          { th: 'Saldo', cls: 'num', td: x => moeda(
              Number(x.valor_total) + Number(x.multa || 0) + Number(x.juros || 0)
              - Number(x.valor_pago || 0)) }
        ], v.slice(0, 8), 'Ninguém em atraso.')}
      </div>

      <div class="cartao" data-ir="financeiro">
        <h3>${icon('comprovante')} Comprovantes para conferir</h3>
        ${tabela([
          { th: 'Inquilino',   td: x => escapeHtml(x.contratos?.inquilinos?.nome || '—') },
          { th: 'Competência', td: x => escapeHtml(competencia(x.competencia)) },
          { th: 'Esperado', cls: 'num', td: x => moeda(x.valor_total) },
          { th: 'Lido', cls: 'num', td: x => x.comprovante_valor_ocr != null
              ? `<strong style="color:var(--ambar)">${moeda(x.comprovante_valor_ocr)}</strong>`
              : badge('não lido', 'cinza') }
        ], c, 'Nenhum comprovante pendente.')}
      </div>

      <div class="cartao" data-ir="notificacoes">
        <h3>${icon('chamado')} Chamados abertos</h3>
        ${tabela([
          { th: '#',        td: x => `#${x.numero}` },
          { th: 'Imóvel',   td: x => escapeHtml(x.imoveis?.codigo || '—') },
          { th: 'Problema', td: x => escapeHtml(x.titulo) },
          { th: 'Aberto há', td: x => {
              const hrs = Math.round((Date.now() - new Date(x.criado_em)) / 36e5);
              const txt = hrs < 48 ? `${hrs}h` : `${Math.round(hrs / 24)}d`;
              return badge(txt, hrs > 72 ? 'vermelho' : hrs > 24 ? 'ambar' : 'cinza');
            } }
        ], ch.slice(0, 8), 'Nenhum chamado aberto.')}
      </div>

      <div class="cartao" data-ir="contratos">
        <h3>${icon('calendario')} Contratos a renovar (90 dias)</h3>
        ${tabela([
          { th: 'Inquilino', td: x => escapeHtml(x.inquilinos?.nome || '—') },
          { th: 'Imóvel',    td: x => escapeHtml(x.imoveis?.codigo || '—') },
          { th: 'Vence em',  td: x => {
              const d = -diasAtraso(x.data_fim);
              return `${data(x.data_fim)} ${badge(`${d}d`, d <= 30 ? 'vermelho' : 'ambar')}`;
            } },
          { th: 'Aluguel', cls: 'num', td: x => moeda(x.valor_aluguel) }
        ], r.slice(0, 8), 'Nenhum contrato vencendo nos próximos 90 dias.')}
      </div>
    </div>

    <!-- Linha 4: tendência -->
    <div class="cartao">
      <h3>${icon('grafico')} Previsto × recebido — últimos 6 meses</h3>
      ${meses.length ? `
        <div class="barras">
          ${meses.map(k => {
            const d = porMes[k];
            const hp = Math.round(d.prev / topo * 100);
            const hr = Math.round(d.rec / topo * 100);
            const p  = d.prev > 0 ? Math.round(d.rec / d.prev * 100) : 0;
            const nome = new Date(k + '-01T12:00:00')
              .toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
            return `
              <div class="col" title="${nome}: previsto ${moeda(d.prev)}, recebido ${moeda(d.rec)} (${p}%)">
                <div style="width:100%;display:flex;gap:3px;align-items:flex-end;height:100%">
                  <div class="bar previsto" style="height:${hp}%"></div>
                  <div class="bar" style="height:${hr}%"></div>
                </div>
                <small>${escapeHtml(nome)}</small>
              </div>`;
          }).join('')}
        </div>
        <div style="display:flex;gap:16px;margin-top:10px;font-size:12px;color:var(--tinta-fraca)">
          <span><i style="display:inline-block;width:10px;height:10px;border-radius:2px;
            background:var(--borda)"></i> previsto</span>
          <span><i style="display:inline-block;width:10px;height:10px;border-radius:2px;
            background:var(--cor-primaria)"></i> recebido</span>
        </div>`
      : '<p class="vazio">Ainda não há histórico de cobranças.</p>'}
    </div>`;

  // Cada cartão de fila leva à aba que resolve o problema.
  el.querySelectorAll('[data-ir]').forEach(card => {
    const aba = card.dataset.ir;
    if (!estado.permissoes[aba]) return;
    card.style.cursor = 'pointer';
    card.onclick = () => abrir(aba);
  });
}
